/**
 * Trunnion AI - Human Gate Node
 * Approval checkpoint node.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { UserCheck, Clock } from 'lucide-react';
import type { HumanGateNodeData } from '@/types';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

export const HumanGateNode = memo(function HumanGateNode({
  id,
  data,
  selected,
}: NodeProps & { data: HumanGateNodeData }) {
  const setSelectedNodeId = useWorkflowDesignerStore((s) => s.setSelectedNodeId);

  return (
    <div
      className={`min-w-55 rounded-xl border-2 bg-trunnion-slate-800 shadow-lg transition-all ${
        selected
          ? 'border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
          : 'border-purple-500/40 hover:border-purple-500/60'
      }`}
      onClick={() => setSelectedNodeId(id)}
    >
      <div className="flex items-center gap-2 rounded-t-[10px] border-b border-trunnion-slate-700 bg-purple-500/10 px-3 py-2">
        <UserCheck className="h-4 w-4 text-purple-400" />
        <span className="text-sm font-semibold text-white">
          {data.label || 'Approval Gate'}
        </span>
      </div>

      <div className="px-3 py-2.5 space-y-1.5">
        <p className="text-xs text-trunnion-slate-300 line-clamp-2">
          {data.description || 'Requires human approval'}
        </p>
        <div className="flex items-center gap-2 text-xs text-trunnion-slate-400">
          <Clock className="h-3 w-3" />
          <span>Timeout: {data.timeoutMinutes}m</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {data.approverRoles.map((role) => (
            <span
              key={role}
              className="rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] text-purple-300"
            >
              {role}
            </span>
          ))}
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-trunnion-slate-800 !border-purple-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-trunnion-slate-800 !border-purple-500"
      />
    </div>
  );
});
